// @ts-strict-ignore
import { RateMode } from 'types'
import { uniq } from 'lodash'
import { JobStatus } from '__generated__/JobCard_Job.graphql'
import { formatAsCurrency } from './formatting'
import { convertRateMode } from './rate'

export function normalizeGroupIndexes<T extends { groupIndex?: number }>(items: T[]): T[] {
  /*
  we have an array like:
  [{ ..., groupIndex: 0 }, { ..., groupIndex: 0 }, { ..., groupIndex: 2 }]
  and this function transforms it to:
  [{ ..., groupIndex: 0 }, { ..., groupIndex: 0 }, { ..., groupIndex: 1 }]
  */
  const groups = uniq(items.map(s => s?.groupIndex || 0)).sort()
  return items.map(s => ({ ...s, groupIndex: groups.indexOf(s.groupIndex) }))
}

export interface IGroup<T> { index: number, items: T[] }

export function getGroupsByIndex<T extends { groupIndex?: number }>(items: T[]): IGroup<T>[] {
  if (!items || !items?.length) return []

  const groups: IGroup<T>[] = []

  normalizeGroupIndexes(items).sort((a, b) => (a.groupIndex || 0) - (b.groupIndex || 0)).forEach((jobSkill) => {
    const group = groups.find(g => g.index === (jobSkill.groupIndex || 0))
    const item = { groupIndex: 0, ...jobSkill }
    if (group) {
      group.items.push(item)
    } else {
      groups.push({ index: jobSkill.groupIndex || 0, items: [item] })
    }
  })

  // We sort groups by index then we rewrite the index so that it's progressive
  // and starts from zero. This is needed to avoid UI bugs on job editing.
  return groups.sort((a, b) => a.index - b.index).map((g, index) => ({
    ...g,
    index,
  }))
}

export function getAnnualCompensationText(minAnnualCompensation: number, maxAnnualCompensation: number) : string {
  const min = Math.floor(minAnnualCompensation / 1000) || 0
  const max = Math.floor(maxAnnualCompensation / 1000) || 0

  if (min) {
    if (max) {
      if (min === max) return `$${min}k/year`
      return `$${min}k to $${max}k/year`
    }

    return `from $${min}k/year`
  }

  if (max) {
    return `$${max}k max/year`
  }

  return null
}

export function getJobTitleWithStatus(job: { status: JobStatus, title: string }) {
  if (!job) return null

  let title = job.title
  if (job.status === 'closed' && !/ \(Closed\)$/.test(job.title)) title += ' (Closed)'
  if (job.status === 'draft' && !/ \(Draft\)$/.test(job.title)) title += ' (Draft)'
  return title
}

export function getRateText(minRate: number, maxRate: number, sourceRateMode: RateMode, displayRateMode: RateMode, currencyCode: string, options: { roundHourlyRate?: boolean } = {}): string {
  const formatOptions = { roundToThousands: displayRateMode === 'year', currency: currencyCode }

  const convertedMinRate = convertRateMode(minRate, sourceRateMode, displayRateMode, options)
  const convertedMaxRate = convertRateMode(maxRate, sourceRateMode, displayRateMode, options)

  if (convertedMinRate) {
    if (convertedMinRate === convertedMaxRate) {
      return `${formatAsCurrency(convertedMinRate, formatOptions)}/${displayRateMode}`
    }

    if (convertedMaxRate) {
      return `${formatAsCurrency(convertedMinRate, formatOptions)} to ${formatAsCurrency(convertedMaxRate, formatOptions)}/${displayRateMode}`
    }

    return `from ${formatAsCurrency(convertedMinRate, formatOptions)}/${displayRateMode}`
  }

  if (convertedMaxRate) {
    return `up to ${formatAsCurrency(convertedMaxRate, formatOptions)}/${displayRateMode}`
  }

  return null
}

export function getIntegrationPriceText(activationFeeUsd: number, monthlyFeeUsd: number) {
  let parts = []
  if (activationFeeUsd) parts.push(`${formatAsCurrency(activationFeeUsd, { currency: 'USD' })} activation fee`)
  if (monthlyFeeUsd) parts.push(`${formatAsCurrency(monthlyFeeUsd, { currency: 'USD' })} per month`)

  return parts.join(' plus ')
}

export function jobActionsDisabled(job: { status: JobStatus }) {
  // TODO: Add an exception here if job has greenhouse integration or candidate comes from greenhouse?
  return job?.status === 'draft'
}
